import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/presentation/components/common/Button';
import { LanguageSelector } from '@/presentation/components/common/LanguageSelector';
import { ThemeToggle } from '@/presentation/components/common/ThemeToggle';

export type UserMenuProps = {
  username: string;
  onLogout: () => void;
  onChangePassword: () => void;
};

export const UserMenu: React.FC<UserMenuProps> = ({ username, onLogout, onChangePassword }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const initial = (username.trim().charAt(0) || '?').toUpperCase();

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target || !rootRef.current) return;
      if (!rootRef.current.contains(target)) {
        setOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="relative flex-shrink-0" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="panel-touch rounded-full bg-gradient-to-br from-teal-500 to-teal-700 text-sm font-semibold text-white shadow-md shadow-teal-900/15 ring-2 ring-white/90 transition hover:from-teal-600 hover:to-teal-800 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-2 dark:ring-gray-900 dark:focus:ring-offset-gray-900"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t('layout.openProfile')}
        title={t('layout.openProfile')}
      >
        {initial}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40 bg-slate-950/20 md:hidden" aria-hidden />
          <div
            role="menu"
            className="fixed inset-x-3 top-[3.75rem] z-50 overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-2xl shadow-slate-900/15 dark:border-gray-700 dark:bg-gray-800 md:absolute md:inset-x-auto md:right-0 md:top-full md:mt-2 md:w-72"
          >
            <div className="border-b border-slate-100 bg-gradient-to-br from-slate-50 to-white px-4 py-3.5 dark:border-gray-700 dark:from-gray-900 dark:to-gray-800">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-teal-500 to-teal-700 text-sm font-semibold text-white">
                  {initial}
                </span>
                <div className="min-w-0">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">
                    {t('layout.profile')}
                  </p>
                  <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                    {username}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-1 px-2 py-2">
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center rounded-xl px-2.5 py-2 text-left text-sm text-slate-600 transition hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-gray-700"
                onClick={() => {
                  setOpen(false);
                  onChangePassword();
                }}
              >
                {t('layout.changePassword')}
              </button>
              <div className="flex items-center justify-between gap-3 rounded-xl px-2.5 py-2">
                <span className="text-sm text-slate-600 dark:text-slate-300">
                  {t('layout.language')}
                </span>
                <LanguageSelector />
              </div>
              <div className="flex items-center justify-between gap-3 rounded-xl px-2.5 py-2">
                <span className="text-sm text-slate-600 dark:text-slate-300">
                  {t('layout.theme')}
                </span>
                <ThemeToggle />
              </div>
            </div>

            <div className="border-t border-slate-100 px-3 py-3 dark:border-gray-700">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="w-full"
                onClick={() => {
                  setOpen(false);
                  onLogout();
                }}
              >
                {t('auth.logout')}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
